import pages from 'pages';
import React, { useMemo, useState } from 'react';
import Header from 'shared/components/Header/Header';
import { resolvePath, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { FAQCategory } from 'modules/support/components/FAQTiles/FAQTiles';
import { Text } from 'shared/components/Text/Text';
import { useGetFAQs } from 'api/faqs';
import filterFAQs from 'modules/overview/utils/filterFAQs';
import useUser from 'hooks/useUser';
import { useTheme } from 'styled-components';
import { useBreakpoints } from 'hooks/useWindowSize';
import { handleFocusedKeyPress } from 'shared/utils/handleFocusedKeypress';
import { FAQSearchBar } from 'modules/support/components/FAQSearchBar/FAQSearchBar';
import { FAQSearchResults } from 'modules/support/components/FAQSearchResults/FAQSearchResults';
import { SubcategoryIcon } from './SubcategoryIcon';
import {
  FAQCategoryBody,
  FAQCategoryWrapper,
  SubcategoriesWrapper,
  SubcategoryWrapper,
} from './CategorySubpage.styles';
import { FAQCategoryStrings } from './types';

const FAQ_CATEGORY_DETAILS: Record<string, FAQCategoryStrings> = {
  [FAQCategory.ACCOUNT]: {
    i18nKey: 'account',
    pageName: 'ACCOUNT_SUPPORT',
    subcategoryTags: ['security'],
  },
  [FAQCategory.PAYING_BILL]: {
    i18nKey: 'billPayments',
    pageName: 'BILL_PAYMENTS',
    subcategoryTags: ['bill_payment', 'automatic_payments', 'financing_and_ownership'],
  },
  [FAQCategory.PAYMENT_CALCULATIONS]: {
    i18nKey: 'paymentCalculations',
    pageName: 'PAYMENT_CALCULATIONS',
    subcategoryTags: ['calculations', 'payment_increases'],
  },
  [FAQCategory.REAMORTIZATION]: {
    i18nKey: 'reamortization',
    pageName: 'REAMORTIZATION',
    subcategoryTags: ['overview', 'payments'],
  },
  [FAQCategory.SYSTEM_CHANGES]: {
    i18nKey: 'systemChanges',
    pageName: 'SYSTEM_CHANGES',
    subcategoryTags: ['upgrades_and_add_ons', 'home_and_ownership_changes'],
  },
  [FAQCategory.SYSTEM_PERFORMANCE]: {
    i18nKey: 'systemPerformance',
    pageName: 'SYSTEM_PERFORMANCE',
    subcategoryTags: ['system_maintenance', 'troubleshooting'],
  },
};

export function CategorySubpage({ category }: { category: FAQCategory }) {
  const { i18nKey, pageName, subcategoryTags } = FAQ_CATEGORY_DETAILS[category];

  const [searchQuery, setSearchQuery] = useState('');
  const [displaySearchResults, setDisplaySearchResults] = useState(false);

  const theme = useTheme();
  const screenSize = useBreakpoints();
  const { t } = useTranslation('translation', {
    keyPrefix: `support&FAQs.faqCategories.${i18nKey}`,
  });
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { data: faqData } = useGetFAQs();
  const { userResult } = useUser();

  const pageTitle = displaySearchResults
    ? t('searchResults', { keyPrefix: 'support&FAQs' })
    : t('title');

  const categoryFAQs = useMemo(
    () =>
      filterFAQs(faqData || null, userResult?.user)?.filter((faq) =>
        faq.tags.some((tag) => tag.tag === category),
      ),
    [faqData, userResult?.user, category],
  );

  const subcategories: string[] = t('subcategories', {
    returnObjects: true,
  }) as string[];

  const handleGoToQuestion = (id: string) => {
    const faqPath = pathname.replace(pages[pageName], pages.FAQ_QUESTION.replace(':faqId', id));
    navigate(faqPath, { state: { originTitle: t('title'), originPath: pathname } });
  };

  const searchResultsView = useMemo(
    () => (
      <FAQSearchResults
        searchQuery={searchQuery}
        onHideSearchResults={() => setDisplaySearchResults(false)}
        isVisible={displaySearchResults}
      />
    ),
    [searchQuery, displaySearchResults],
  );

  return (
    <FAQCategoryWrapper>
      <Header
        pageName={pageName}
        title={pageTitle}
        routeBreadCrumbs={[
          {
            label: t('support&FAQs.support', { keyPrefix: '' }),
            link: resolvePath(pages.SUPPORT),
          },
          { label: pageTitle },
        ]}
      />
      <FAQSearchBar onChange={setSearchQuery} onSearch={() => setDisplaySearchResults(true)} />
      <FAQCategoryBody>
        {displaySearchResults ? (
          searchResultsView
        ) : (
          <SubcategoriesWrapper>
            {subcategories.map((subcategory, idx) => (
              <SubcategoryWrapper key={subcategory}>
                <SubcategoryIcon subcategory={subcategoryTags[idx]} />
                <Text as="h3" $fontWeight={screenSize === 'lg' || screenSize === 'xl' ? 500 : 700}>
                  {subcategory}
                </Text>
                {categoryFAQs
                  ?.filter((faq) =>
                    faq.tags.some((tag) => tag.tag === `subcategory:${subcategoryTags[idx]}`),
                  )
                  .map((faq) => (
                    <Text
                      as="p"
                      $fontWeight={400}
                      color={theme.colors.textSecondaryGrey90}
                      key={faq.id}
                      tabIndex={0}
                      onClick={() => handleGoToQuestion(faq.id)}
                      onKeyUp={(e) => handleFocusedKeyPress(e, () => handleGoToQuestion(faq.id))}
                    >
                      {faq.question}
                    </Text>
                  ))}
              </SubcategoryWrapper>
            ))}
          </SubcategoriesWrapper>
        )}
      </FAQCategoryBody>
    </FAQCategoryWrapper>
  );
}

export default CategorySubpage;
